import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterGroup from "@/pages/category/components/FilterGroup";
import {
  ALL,
  filterMovies,
  getGenres,
  getRegions,
  getYearOptions,
  paginate,
  sortMovies,
  type SortKey,
} from "@/utils/movieUtils";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

const TYPE_OPTIONS = ["电影", "电视剧"];
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "update", label: "按更新时间" },
  { key: "heat", label: "按热度" },
  { key: "rating", label: "按评分" },
];

export default function Category() {
  const [params, setParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const type = params.get("type") || ALL;
  const genre = params.get("genre") || ALL;
  const region = params.get("region") || ALL;
  const year = params.get("year") || ALL;
  const sort = (params.get("sort") as SortKey) || "update";
  const page = Number(params.get("page") || "1");

  useEffect(() => {
    setFilterOpen(false);
  }, [params]);

  const genres = useMemo(() => getGenres(), []);
  const regions = useMemo(() => getRegions(), []);
  const years = useMemo(() => getYearOptions(), []);

  const filtered = useMemo(
    () => sortMovies(filterMovies({ type, genre, region, year }), sort),
    [type, genre, region, year, sort]
  );

  const { items, current, totalPages, total } = paginate(filtered, page, PAGE_SIZE);

  const updateParam = (key: string, value: string, resetPage = true) => {
    const next = new URLSearchParams(params);
    if (!value || value === ALL) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (resetPage) next.delete("page");
    setParams(next);
  };

  const changeType = (value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === ALL) next.delete("type");
    else next.set("type", value);
    next.delete("genre");
    next.delete("page");
    setParams(next);
  };

  const changeSort = (value: SortKey) => {
    const next = new URLSearchParams(params);
    if (value === "update") next.delete("sort");
    else next.set("sort", value);
    next.delete("page");
    setParams(next);
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (type !== ALL) next.set("type", type);
    setParams(next);
  };

  const changePage = (nextPage: number) => {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pageTitle =
    type === "电影" ? "电影分类" : type === "电视剧" ? "电视剧分类" : "全部影视分类";

  usePageMeta({
    title: `${pageTitle} - 岳母的诱惑`,
    description: `岳母的诱惑${pageTitle}，汇集丰富的${type === ALL ? "电影与电视剧" : type}内容，可按类型、地区、年份筛选，查看详细的剧情介绍与更新信息。`,
    keywords: `岳母的诱惑,${type === ALL ? "电影,电视剧" : type},影视分类,剧情介绍,影视筛选`,
  });

  const hasActiveFilter =
    genre !== ALL || region !== ALL || year !== ALL;

  const panel = (
    <div className="space-y-6">
      <FilterGroup
        title="内容类型"
        options={TYPE_OPTIONS}
        value={type}
        onChange={changeType}
      />
      <div className="h-px bg-background-800" />
      <FilterGroup
        title="影片类型"
        options={genres}
        value={genre}
        onChange={(v) => updateParam("genre", v)}
      />
      <div className="h-px bg-background-800" />
      <FilterGroup
        title="地区"
        options={regions}
        value={region}
        onChange={(v) => updateParam("region", v)}
      />
      <div className="h-px bg-background-800" />
      <FilterGroup
        title="年份"
        options={years.filter((y) => y !== ALL)}
        value={year}
        onChange={(v) => updateParam("year", v)}
      />
      {hasActiveFilter && (
        <button
          type="button"
          onClick={resetFilters}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md border border-background-700 text-sm text-foreground-300 hover:border-primary-500 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-8 md:pt-10 pb-16">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类" },
            { label: type === ALL ? "全部影视" : type },
          ]}
        />

        <header className="mt-5 mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-50">
            {pageTitle}
          </h1>
          <p className="mt-2 text-sm text-foreground-500 max-w-2xl leading-relaxed">
            浏览「岳母的诱惑」中的{type === ALL ? "全部" : type}内容，
            通过类型、地区、年份等条件细化筛选，快速找到你想看的影视作品。
          </p>
        </header>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 p-5 rounded-lg bg-background-900/60 border border-background-800">
              <div className="flex items-center gap-2 mb-5">
                <i className="ri-filter-3-line text-accent-400"></i>
                <span className="font-heading font-bold text-foreground-100">
                  筛选条件
                </span>
              </div>
              {panel}
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-900 border border-background-800 text-sm text-foreground-200 hover:border-primary-500 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line"></i>
                  筛选
                  {hasActiveFilter && (
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
                  )}
                </button>
                <p className="text-sm text-foreground-500">
                  共
                  <span className="text-accent-400 font-medium mx-1">{total}</span>
                  部作品
                </p>
              </div>

              <div className="flex items-center gap-2">
                <i className="ri-sort-desc text-foreground-500 hidden sm:block"></i>
                <select
                  value={sort}
                  onChange={(e) => changeSort(e.target.value as SortKey)}
                  className="bg-background-900 border border-background-800 rounded-md text-sm text-foreground-200 px-3 py-2 outline-none focus:border-primary-500 cursor-pointer"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {total === 0 ? (
              <EmptyState
                icon="ri-film-line"
                title="未找到符合条件的影视作品"
                description="试试放宽筛选条件，或重置后重新选择类型、地区与年份。"
                action={
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选
                  </button>
                }
              />
            ) : (
              <>
                <MovieGrid items={items} />
                <Pagination current={current} totalPages={totalPages} onChange={changePage} />
              </>
            )}
          </div>
        </div>
      </div>

      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-background-950/80 backdrop-blur-sm"
            onClick={() => setFilterOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto bg-background-900 border-t border-background-700 rounded-t-2xl p-5 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <span className="font-heading font-bold text-foreground-50">
                筛选条件
              </span>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭筛选"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-300 bg-background-800 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {panel}
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-6 w-full py-3 rounded-md bg-primary-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              查看{total}部结果
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}